import { useMemo } from "react";
import { useSelector } from "react-redux";
import { selectPermissions, selectUserRole } from "../store/slices/authSlice";

/**
 * Custom hook for checking user permissions
 * @returns {Object} Permission checking utilities
 */
export const usePermissions = () => {
  const permissions = useSelector(selectPermissions);
  const userRole = useSelector(selectUserRole);

  const permissionMap = useMemo(() => {
    const map = {};
    permissions.forEach((perm) => {
      const key = `${perm.module}:${perm.action}`;
      map[key] = perm.is_active === "Y";
    });
    return map;
  }, [permissions]);

  /**
   * Check if user has specific permission
   * @param {string} module - Module name (e.g., "STUDENT", "FEES")
   * @param {string} action - Action name (e.g., "CREATE", "READ", "UPDATE")
   * @returns {boolean} True if user has permission
   */
  const hasPermission = (module, action) => {
    const key = `${module}:${action}`;
    return permissionMap[key] === true;
  };

  /**
   * Check if user has any of the specified permissions
   * @param {Array<{module: string, action: string}>} permissionList
   * @returns {boolean} True if user has any permission
   */
  const hasAnyPermission = (permissionList) => {
    return permissionList.some((perm) =>
      hasPermission(perm.module, perm.action)
    );
  };

  /**
   * Check if user has all specified permissions
   * @param {Array<{module: string, action: string}>} permissionList
   * @returns {boolean} True if user has all permissions
   */
  const hasAllPermissions = (permissionList) => {
    return permissionList.every((perm) =>
      hasPermission(perm.module, perm.action)
    );
  };

  /**
   * Check if user has specific role
   * @param {string|Array<string>} roles - Role name(s) to check
   * @returns {boolean} True if user has role
   */
  const hasRole = (roles) => {
    if (Array.isArray(roles)) {
      return roles.includes(userRole);
    }
    return userRole === roles;
  };

  /**
   * Check if user has role OR permission
   * @param {string|Array<string>} roles - Role name(s)
   * @param {string} module - Module name
   * @param {string} action - Action name
   * @returns {boolean} True if user has role or permission
   */
  const hasRoleOrPermission = (roles, module, action) => {
    return hasRole(roles) || hasPermission(module, action);
  };

  /**
   * Get all permissions for a specific module
   * @param {string} module - Module name
   * @returns {Array<string>} List of actions user can perform on module
   */
  const getModulePermissions = (module) => {
    return permissions
      .filter((perm) => perm.module === module && perm.is_active === "Y")
      .map((perm) => perm.action);
  };

  return {
    permissions,
    userRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasRoleOrPermission,
    getModulePermissions,
  };
};

export default usePermissions;
